import { z } from "zod";

import { StringToIntIdSchema } from "../common/dto";

export const LimitationTypeSchema = z.enum(["number", "list", "boolean"]);
export type LimitationType = z.infer<typeof LimitationTypeSchema>;

export const LimitationOperatorSchema = z.enum(["<", ">"]);
export type LimitationOperator = z.infer<typeof LimitationOperatorSchema>;

export const LimitationValueSchema = z.union([z.string(), z.number(), z.boolean()]);
export type LimitationValue = z.infer<typeof LimitationValueSchema>;

export const LimitationOptionSchema = z.object({
  id: z.number().int().positive(),
  definitionId: z.number().int().positive(),
  value: LimitationValueSchema,
  sortOrder: z.number().int().nonnegative(),
  archived: z.boolean(),
  usageCount: z.number().int().nonnegative(),
});

export type LimitationOption = z.infer<typeof LimitationOptionSchema>;

export const LimitationDefinitionSchema = z.object({
  id: z.number().int().positive(),
  crewId: z.number().int().positive(),
  code: z.string().min(1).max(191),
  name: z.string().min(1).max(191),
  type: LimitationTypeSchema,
  operator: LimitationOperatorSchema.nullable(),
  unit: z.string().min(1).max(32).nullable(),
  archived: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  usageCount: z.number().int().nonnegative(),
  options: z.array(LimitationOptionSchema),
});

export type LimitationDefinition = z.infer<typeof LimitationDefinitionSchema>;

export const LimitationDefinitionsResponseSchema = z.array(LimitationDefinitionSchema);
export type LimitationDefinitionsResponse = z.infer<typeof LimitationDefinitionsResponseSchema>;

export const LimitationUsageEntrySchema = z.object({
  userId: z.number().int().positive(),
  userName: z.string(),
  aircraftTypeId: z.number().int().positive(),
  aircraftTypeName: z.string(),
  operationId: z.number().int().positive(),
  operationName: z.string(),
  optionId: z.number().int().positive(),
  value: LimitationValueSchema,
  displayValue: z.string(),
});

export type LimitationUsageEntry = z.infer<typeof LimitationUsageEntrySchema>;

export const LimitationUsageResponseSchema = z.array(LimitationUsageEntrySchema);
export type LimitationUsageResponse = z.infer<typeof LimitationUsageResponseSchema>;

export const LimitationConfigSchema = z.object({
  limitationSetsEnabled: z.boolean(),
});

export type LimitationConfig = z.infer<typeof LimitationConfigSchema>;

export const UpdateLimitationConfigBodySchema = LimitationConfigSchema;
export type UpdateLimitationConfigBody = z.infer<typeof UpdateLimitationConfigBodySchema>;

export const LimitationIdParamSchema = z.object({
  crewId: StringToIntIdSchema,
  limitationId: StringToIntIdSchema,
});

export type LimitationIdParam = z.infer<typeof LimitationIdParamSchema>;

export const CreateLimitationOptionSchema = z.object({
  value: LimitationValueSchema,
  sortOrder: z.number().int().nonnegative().optional(),
});

export type CreateLimitationOption = z.infer<typeof CreateLimitationOptionSchema>;

export const UpdateLimitationOptionSchema = CreateLimitationOptionSchema.extend({
  id: z.number().int().positive().optional(),
  archived: z.boolean().optional(),
});

export type UpdateLimitationOption = z.infer<typeof UpdateLimitationOptionSchema>;

const validateLimitationDefinitionShape = (
  body: {
    type?: LimitationType;
    operator?: LimitationOperator | null;
    options?: Array<{ value?: LimitationValue }>;
  },
  ctx: z.RefinementCtx,
) => {
  if (body.type === "number" && !body.operator) {
    ctx.addIssue({ code: "custom", path: ["operator"], message: "zod.limitations.errors.numeric_operator_required" });
  }
  if ((body.type === "list" || body.type === "boolean") && body.operator) {
    ctx.addIssue({ code: "custom", path: ["operator"], message: "zod.limitations.errors.operator_not_supported" });
  }
  if (!body.options || !body.type) return;
  if (body.options.length === 0) {
    ctx.addIssue({ code: "custom", path: ["options"], message: "zod.limitations.errors.option_required" });
    return;
  }
  const seenOptionValues = new Set<string>();

  body.options.forEach((option, index) => {
    const valueKey = body.type === "list" && typeof option.value === "string"
      ? option.value.trim().toLowerCase()
      : String(option.value);
    if (seenOptionValues.has(valueKey)) {
      ctx.addIssue({
        code: "custom",
        path: ["options", index, "value"],
        message: "zod.limitations.errors.option_duplicate",
      });
    }
    seenOptionValues.add(valueKey);

    if (body.type === "number" && typeof option.value !== "number") {
      ctx.addIssue({
        code: "custom",
        path: ["options", index, "value"],
        message: "zod.limitations.errors.numeric_option_invalid",
      });
    }
    if (body.type === "boolean" && typeof option.value !== "boolean") {
      ctx.addIssue({
        code: "custom",
        path: ["options", index, "value"],
        message: "zod.limitations.errors.boolean_option_invalid",
      });
    }
    if (body.type === "boolean" && option.value !== true) {
      ctx.addIssue({
        code: "custom",
        path: ["options", index, "value"],
        message: "zod.limitations.errors.boolean_option_must_be_true",
      });
    }
    if (body.type === "list" && typeof option.value !== "string") {
      ctx.addIssue({
        code: "custom",
        path: ["options", index, "value"],
        message: "zod.limitations.errors.list_option_invalid",
      });
    }
  });
};

const CreateLimitationDefinitionBodyBaseSchema = z.object({
  code: z.string().min(1).max(191),
  name: z.string().min(1).max(191),
  type: LimitationTypeSchema,
  operator: LimitationOperatorSchema.nullable().optional(),
  unit: z.string().min(1).max(32).nullable().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  options: z.array(CreateLimitationOptionSchema).default([]),
});

export const CreateLimitationDefinitionBodySchema = CreateLimitationDefinitionBodyBaseSchema.superRefine(
  validateLimitationDefinitionShape,
);

export type CreateLimitationDefinitionBody = z.infer<typeof CreateLimitationDefinitionBodySchema>;

export const UpdateLimitationDefinitionBodySchema = z.object({
  code: z.string().min(1).max(191).optional(),
  name: z.string().min(1).max(191).optional(),
  type: LimitationTypeSchema.optional(),
  operator: LimitationOperatorSchema.nullable().optional(),
  unit: z.string().min(1).max(32).nullable().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  options: z.array(UpdateLimitationOptionSchema).optional(),
}).superRefine(validateLimitationDefinitionShape);

export type UpdateLimitationDefinitionBody = z.infer<typeof UpdateLimitationDefinitionBodySchema>;

export const parseCompleteLimitationDefinitionBody = (body: CreateLimitationDefinitionBody): CreateLimitationDefinitionBody =>
  CreateLimitationDefinitionBodySchema.parse(body);

export const AssignedLimitationInputSchema = z.object({
  definitionId: z.number().int().positive(),
  optionId: z.number().int().positive(),
});

export type AssignedLimitationInput = z.infer<typeof AssignedLimitationInputSchema>;

export const AssignedLimitationSchema = z.object({
  definitionId: z.number().int().positive(),
  definitionCode: z.string(),
  definitionName: z.string(),
  type: LimitationTypeSchema,
  operator: LimitationOperatorSchema.nullable(),
  unit: z.string().nullable(),
  optionId: z.number().int().positive(),
  value: LimitationValueSchema,
  displayValue: z.string(),
});

export type AssignedLimitation = z.infer<typeof AssignedLimitationSchema>;

export const AssignedLimitationSnapshotSchema = AssignedLimitationSchema.extend({
  changedFromDisplayValue: z.string().nullable().optional(),
});

export type AssignedLimitationSnapshot = z.infer<typeof AssignedLimitationSnapshotSchema>;
